import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { useTokenCheck } from '../hooks/useTokenCheck';
import { Calendar, X, Mic, Sparkles, RotateCcw, Check } from 'lucide-react';
import { offlineDataCache, CACHE_KEYS } from '../utils/offlineDataCache';
import { offlineSync } from '../utils/offlineSync';
import { formatDate as formatDateUtil } from '../utils/dateUtils';

interface Staff {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

interface LeaveType {
  _id: string;
  name: string;
  typeOfUnits: 'hours' | 'days';
  isPaid: boolean;
}

interface LeaveRequestFormProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export const LeaveRequestForm: React.FC<LeaveRequestFormProps> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { handleTokenError } = useTokenCheck();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // Form state
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dailyHours, setDailyHours] = useState<{ [date: string]: number }>({});
  const [description, setDescription] = useState('');
  
  // Description AI/voice state
  const [isRecordingDescription, setIsRecordingDescription] = useState<boolean>(false);
  const [isFormattingDescriptionAI, setIsFormattingDescriptionAI] = useState<boolean>(false);
  const [originalDescription, setOriginalDescription] = useState<string>('');
  const [aiFormattedDescription, setAiFormattedDescription] = useState<string>('');
  const [showAIDescriptionPreview, setShowAIDescriptionPreview] = useState<boolean>(false);
  
  // Data state
  const [staff, setStaff] = useState<Staff[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<Array<{
    leaveTypeId: { _id: string; name: string; typeOfUnits: string };
    balance: number;
    typeOfUnits: string;
  }>>([]);
  const [xeroIntegration, setXeroIntegration] = useState<{ isActive: boolean; handlePayroll: boolean } | null>(null);
  const [myobIntegration, setMyobIntegration] = useState<{ isActive: boolean; handlePayroll: boolean } | null>(null);
  const [isFetchingPayrollBalance, setIsFetchingPayrollBalance] = useState(false);
  const [failedFetchAttempts, setFailedFetchAttempts] = useState<Set<string>>(new Set());
  const [payrollSettings, setPayrollSettings] = useState<{
    defaultPayFrequency?: 'weekly' | 'fortnightly' | 'monthly';
    payPeriodEndDay?: number;
  }>({});
  
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    // Always try to fetch fresh data when form opens (especially after server reset)
    // Reset state first to ensure clean slate
    setStaff([]);
    setLeaveTypes([]);
    fetchInitialData(true);
    fetchAccountSettings();
    
    // Check if there's a pending date from the event
    const pendingDate = (window as any).__pendingLeaveFormDate;
    if (pendingDate) {
      const dateStr = pendingDate.toISOString().split('T')[0];
      setStartDate(dateStr);
      setEndDate(dateStr);
      delete (window as any).__pendingLeaveFormDate;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check Xero and MYOB integration status
  useEffect(() => {
    const checkIntegrations = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const [xeroStatusRes, xeroSyncRes, myobSyncRes] = await Promise.all([
          fetch(`${apiUrl}/api/xero/status`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${apiUrl}/api/xero/sync-status`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${apiUrl}/api/myob/sync-status`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);

        if (xeroStatusRes.ok) {
          const xeroStatusData = await xeroStatusRes.json();
          if (xeroStatusData.success && xeroStatusData.data?.connected && xeroSyncRes.ok) {
            const xeroSyncData = await xeroSyncRes.json();
            if (xeroSyncData.success && xeroSyncData.data) {
              setXeroIntegration({
                isActive: true,
                handlePayroll: xeroSyncData.data.handlePayroll || false
              });
            } else {
              setXeroIntegration(null);
            }
          } else {
            setXeroIntegration(null);
          }
        } else {
          setXeroIntegration(null);
        }

        if (myobSyncRes.ok) {
          const myobData = await myobSyncRes.json();
          if (myobData.success && myobData.data?.connected && myobData.data?.handlePayroll) {
            setMyobIntegration({ isActive: true, handlePayroll: true });
          } else {
            setMyobIntegration(null);
          }
        } else {
          setMyobIntegration(null);
        }
      } catch (error) {
        console.error('[LeaveRequestForm] Error checking payroll integrations:', error);
      }
    };

    checkIntegrations();
  }, [apiUrl]);

  // Fetch leave balances when staff is selected
  useEffect(() => {
    if (!selectedStaffId) {
      setLeaveBalances([]);
      return;
    }

    const fetchLeaveBalances = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${apiUrl}/api/leave-requests/balances/${selectedStaffId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            console.log('[LeaveRequestForm] Fetched leave balances:', data.data);
            console.log('[LeaveRequestForm] Leave balances structure:', data.data.map((lb: any) => ({
              leaveTypeId: typeof lb.leaveTypeId === 'object' ? lb.leaveTypeId._id : lb.leaveTypeId,
              leaveTypeName: typeof lb.leaveTypeId === 'object' ? lb.leaveTypeId.name : 'Unknown',
              balance: lb.balance,
              typeOfUnits: lb.typeOfUnits
            })));
            setLeaveBalances(data.data);
          } else {
            console.log('[LeaveRequestForm] No leave balances in response');
            setLeaveBalances([]);
          }
        } else {
          console.log('[LeaveRequestForm] Failed to fetch leave balances:', response.status);
          setLeaveBalances([]);
        }
      } catch (error) {
        console.error('[LeaveRequestForm] Error fetching leave balances:', error);
        setLeaveBalances([]);
      }
    };

    fetchLeaveBalances();
  }, [selectedStaffId, apiUrl]);

  const payrollActive = (xeroIntegration?.isActive && xeroIntegration?.handlePayroll) || (myobIntegration?.isActive && myobIntegration?.handlePayroll);

  // Fetch balance from payroll (Xero or MYOB) when leave type is selected and balance not found
  useEffect(() => {
    if (!selectedStaffId || !selectedLeaveTypeId || !payrollActive) {
      return;
    }
    
    if (isFetchingPayrollBalance) {
      return; // Already fetching, skip
    }

    const fetchBalanceFromPayroll = async () => {
      // Create a unique key for this staff+leaveType combination
      const fetchKey = `${selectedStaffId}-${selectedLeaveTypeId}`;
      
      // Check if we've already tried to fetch this and it failed (to prevent infinite loops)
      if (failedFetchAttempts.has(fetchKey)) {
        console.log('[LeaveRequestForm] Already attempted to fetch this balance and it failed, skipping to prevent loop');
        return;
      }
      
      // Check if balance already exists and has a non-zero value
      const existingBalance = leaveBalances.find((lb: any) => {
        const lbLeaveTypeId = typeof lb.leaveTypeId === 'object' && lb.leaveTypeId !== null
          ? (lb.leaveTypeId._id || lb.leaveTypeId)
          : lb.leaveTypeId;
        return String(lbLeaveTypeId) === String(selectedLeaveTypeId);
      });
      
      // Only skip if balance exists AND is greater than 0
      if (existingBalance && existingBalance.balance > 0) {
        console.log('[LeaveRequestForm] Balance already exists with non-zero value, skipping Xero fetch');
        // Remove from failed attempts if we now have a valid balance
        setFailedFetchAttempts(prev => {
          const next = new Set(prev);
          next.delete(fetchKey);
          return next;
        });
        return;
      }
      
      // If balance is 0 or doesn't exist, fetch from Xero
      if (existingBalance && existingBalance.balance === 0) {
        console.log('[LeaveRequestForm] Balance exists but is 0, fetching from payroll to update');
      } else {
        console.log('[LeaveRequestForm] Balance not found, fetching from payroll');
      }

      setIsFetchingPayrollBalance(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsFetchingPayrollBalance(false);
          return;
        }

        const response = await fetch(`${apiUrl}/api/leave-requests/fetch-balance/${selectedStaffId}/${selectedLeaveTypeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            console.log('[LeaveRequestForm] Balance fetched from payroll:', data.data);
            
            // If balance is still 0 after fetch, mark this as a failed attempt to prevent loops
            if (data.data.balance === 0) {
              console.log('[LeaveRequestForm] Balance is still 0 after payroll fetch, marking as failed attempt');
              setFailedFetchAttempts(prev => new Set(prev).add(fetchKey));
            } else {
              // Remove from failed attempts if we got a valid balance
              setFailedFetchAttempts(prev => {
                const next = new Set(prev);
                next.delete(fetchKey);
                return next;
              });
            }
            
            // Refetch balances from database to get the proper structure with populated leaveTypeId
            const refetchResponse = await fetch(`${apiUrl}/api/leave-requests/balances/${selectedStaffId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (refetchResponse.ok) {
              const refetchData = await refetchResponse.json();
              if (refetchData.success && refetchData.data) {
                console.log('[LeaveRequestForm] Refetched balances after payroll fetch:', refetchData.data);
                setLeaveBalances(refetchData.data);
              }
            }
          } else {
            // Mark as failed if response wasn't successful
            setFailedFetchAttempts(prev => new Set(prev).add(fetchKey));
          }
        } else {
          // Mark as failed on HTTP error
          setFailedFetchAttempts(prev => new Set(prev).add(fetchKey));
        }
      } catch (error) {
        console.error('[LeaveRequestForm] Error fetching balance from payroll:', error);
      } finally {
        setIsFetchingPayrollBalance(false);
      }
    };

    fetchBalanceFromPayroll();
    // Note: failedFetchAttempts is intentionally NOT in dependencies to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaffId, selectedLeaveTypeId, payrollActive, apiUrl]);

  const fetchAccountSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (!userStr || !token) return;
      
      const user = JSON.parse(userStr);
      const accountId = user.currentAccountId || user.accountId;
      
      if (!accountId) return;

      // Check cache first
      const cachedSettings = offlineDataCache.get<any>(CACHE_KEYS.ACCOUNT_SETTINGS);
      if (cachedSettings?.payrollSettings) {
        setPayrollSettings(cachedSettings.payrollSettings);
      }

      if (navigator.onLine) {
        const response = await fetch(`${apiUrl}/api/accounts/${accountId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.account?.payrollSettings) {
            setPayrollSettings(data.account.payrollSettings);
            offlineDataCache.set(CACHE_KEYS.ACCOUNT_SETTINGS, data.account);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch account settings:', error);
    }
  };

  // Calculate working days and initialize daily hours when dates change
  useEffect(() => {
    if (startDate && endDate) {
      const workingDays = getWorkingDaysList(startDate, endDate);
      
      setDailyHours(prev => {
        const newDailyHours: { [date: string]: number } = {};
        
        // Initialize each working day with 8 hours default, or keep existing value
        workingDays.forEach(date => {
          newDailyHours[date] = prev[date] || 8;
        });
        
        return newDailyHours;
      });
    } else {
      setDailyHours({});
    }
  }, [startDate, endDate]);

  // Helper function to get working days list (excludes weekends)
  const getWorkingDaysList = (startDate: string, endDate: string): string[] => {
    if (!startDate || !endDate) return [];
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start > end) return [];
    
    const workingDays: string[] = [];
    const current = new Date(start);
    
    while (current <= end) {
      const dayOfWeek = current.getDay();
      // Exclude weekends (Sunday = 0, Saturday = 6)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays.push(current.toISOString().split('T')[0]);
      }
      current.setDate(current.getDate() + 1);
    }
    
    return workingDays;
  };


  // Calculate total hours from daily hours
  const calculateTotalHours = (): number => {
    return Object.values(dailyHours).reduce((sum, hours) => sum + (hours || 0), 0);
  };

  // Handle voice recording for description
  const handleStartRecordingDescription = async () => {
    if (!isOnline) {
      setMessage(t('timesheet.voiceTranscriptionOffline') || 'Voice transcription unavailable offline');
      setMessageType('error');
      return;
    }

    if (isRecordingDescription) {
      setIsRecordingDescription(false);
      return;
    }

    setIsRecordingDescription(true);
    
    try {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      
      if (!SpeechRecognition) {
        setMessage('Speech recognition not supported in this browser');
        setMessageType('error');
        setIsRecordingDescription(false);
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: any) => {
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          }
        }

        if (finalTranscript) {
          setDescription(prev => prev + finalTranscript);
        }
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setMessage(`Speech recognition error: ${event.error}`);
        setMessageType('error');
        setIsRecordingDescription(false);
      };

      recognition.onend = () => {
        setIsRecordingDescription(false);
      };

      recognition.start();
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      setMessage('Failed to start speech recognition');
      setMessageType('error');
      setIsRecordingDescription(false);
    }
  };

  // Handle AI formatting for description
  const handleFormatDescriptionWithAI = async () => {
    if (!isOnline) {
      setMessage(t('timesheet.aiFormattingOffline') || 'AI formatting unavailable offline');
      setMessageType('error');
      return;
    }

    if (!description.trim()) {
      setMessage('Please enter some text to format');
      setMessageType('error');
      return;
    }

    setIsFormattingDescriptionAI(true);
    setOriginalDescription(description);
    
    try {
      const token = localStorage.getItem('token');
      
      const selectedStaffMember = staff.find(s => s._id === selectedStaffId);
      const selectedLeaveType = leaveTypes.find(lt => lt._id === selectedLeaveTypeId);
      
      const context = {
        notes: description.trim(),
        leaveRequest: {
          staff: selectedStaffMember ? `${selectedStaffMember.firstName} ${selectedStaffMember.lastName}` : '',
          leaveType: selectedLeaveType?.name || '',
          startDate,
          endDate,
          totalHours: calculateTotalHours()
        }
      };
      
      const response = await fetch(`${apiUrl}/api/ai/format-notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(context)
      });

      const data = await response.json();

      // Check for token errors (402 status)
      if (handleTokenError(response, data)) {
        // Token purchase modal will be shown automatically
        setMessage(data.message || 'Insufficient tokens. Please purchase more tokens to continue using AI features.');
        setMessageType('error');
        setIsFormattingDescriptionAI(false);
        return;
      }

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to format description');
      }

      setAiFormattedDescription(data.formattedNotes || data.data?.formattedNotes);
      setShowAIDescriptionPreview(true);
    } catch (error) {
      console.error('Failed to format with AI:', error);
      setMessage(error instanceof Error ? error.message : 'Failed to format description with AI. Please try again.');
      setMessageType('error');
    } finally {
      setIsFormattingDescriptionAI(false);
    }
  };

  const handleAcceptAIDescription = () => {
    setDescription(aiFormattedDescription);
    setShowAIDescriptionPreview(false);
    setAiFormattedDescription('');
    setOriginalDescription('');
  };

  const handleRevertAIDescription = () => {
    setDescription(originalDescription);
    setShowAIDescriptionPreview(false);
    setAiFormattedDescription('');
    setOriginalDescription('');
  };

  // Group working days by pay period
  const groupDaysByPayPeriod = (): Array<{ periodLabel: string; days: Array<{ date: string; hours: number }>; total: number }> => {
    if (!startDate || !endDate || Object.keys(dailyHours).length === 0) return [];

    const frequency = payrollSettings.defaultPayFrequency || 'weekly';
    const payPeriodEndDay = payrollSettings.payPeriodEndDay ?? 0;
    const daysInPeriod = frequency === 'weekly' ? 7 : frequency === 'fortnightly' ? 14 : 30;

    // Sort dates
    const sortedDates = Object.entries(dailyHours)
      .map(([date, hours]) => ({ date, hours }))
      .sort((a, b) => a.date.localeCompare(b.date));

    const periods: Array<{ periodLabel: string; days: Array<{ date: string; hours: number }>; total: number }> = [];
    const periodMap = new Map<string, Array<{ date: string; hours: number }>>();

    sortedDates.forEach(({ date, hours }) => {
      const dateObj = new Date(date);
      const dayOfWeek = dateObj.getDay();
      
      // Calculate which pay period this date belongs to
      let daysUntilEndDay = (payPeriodEndDay - dayOfWeek + 7) % 7;
      let periodEnd = new Date(dateObj);
      
      if (daysUntilEndDay === 0) {
        periodEnd.setDate(dateObj.getDate());
      } else {
        periodEnd.setDate(dateObj.getDate() + daysUntilEndDay);
      }
      
      // Calculate period start
      const periodStart = new Date(periodEnd);
      periodStart.setDate(periodEnd.getDate() - (daysInPeriod - 1));
      
      // If date is before period start, adjust
      if (dateObj < periodStart) {
        periodStart.setDate(periodStart.getDate() - daysInPeriod);
        periodEnd.setDate(periodEnd.getDate() - daysInPeriod);
      }
      
      const periodKey = `${periodStart.toISOString().split('T')[0]}_${periodEnd.toISOString().split('T')[0]}`;
      
      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, []);
      }
      
      periodMap.get(periodKey)!.push({ date, hours });
    });

    // Convert to array and calculate totals
    periodMap.forEach((days, periodKey) => {
      const [startStr, endStr] = periodKey.split('_');
      const start = new Date(startStr);
      const end = new Date(endStr);
      
      const periodLabel = frequency === 'monthly'
        ? `${formatDateUtil(start)}`
        : `${formatDateUtil(start)} - ${formatDateUtil(end)}`;
      
      const total = days.reduce((sum, d) => sum + d.hours, 0);
      
      periods.push({
        periodLabel,
        days: days.sort((a, b) => a.date.localeCompare(b.date)),
        total
      });
    });

    return periods.sort((a, b) => a.days[0].date.localeCompare(b.days[0].date));
  };

  // Format day number for calendar display
  const formatDayNumber = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.getDate().toString();
  };

  const fetchInitialData = async (forceRefresh: boolean = false) => {
    const isOnline = navigator.onLine;
    
    // Check cache first (for offline support or initial display)
    const cachedStaff = offlineDataCache.get<any[]>(CACHE_KEYS.STAFF);
    const cachedLeaveTypes = offlineDataCache.get<any[]>(CACHE_KEYS.LEAVE_TYPES);
    
    // Only use cache if not forcing refresh and we have cache
    // When forceRefresh is true, skip cache entirely to ensure fresh data
    if (!forceRefresh) {
      if (cachedStaff) {
        // Filter for active staff only
        setStaff(cachedStaff.filter((s: any) => s._id && s.isActive !== false));
      }
      
      if (cachedLeaveTypes) {
        // Filter for active leave types that can be requested
        setLeaveTypes(cachedLeaveTypes.filter((lt: any) => lt.isActive !== false && lt.canBeRequested !== false));
      }
    }

    // If online, always try to fetch fresh data (especially after server reset)
    // When forceRefresh is true, always fetch even if cache exists
    if (isOnline) {
      try {
        const token = localStorage.getItem('token');
        
        const [staffRes, leaveTypesRes] = await Promise.all([
          fetch(`${apiUrl}/api/staff`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${apiUrl}/api/leave-types?forRequests=true`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        const staffData = await staffRes.json();
        const leaveTypesData = await leaveTypesRes.json();

        if (staffData.success) {
          // Filter for active staff only
          const staffList = Array.isArray(staffData.data) 
            ? staffData.data.filter((s: any) => s._id && s.isActive !== false)
            : [];
          offlineDataCache.set(CACHE_KEYS.STAFF, staffList);
          setStaff(staffList);
        }
        
        if (leaveTypesData.success) {
          // API returns data as an array directly, not nested in leaveTypes
          // Filter for active leave types that can be requested
          const leaveTypesList = Array.isArray(leaveTypesData.data) 
            ? leaveTypesData.data.filter((lt: any) => lt.isActive !== false && lt.canBeRequested !== false)
            : (leaveTypesData.data?.leaveTypes || []).filter((lt: any) => lt.isActive !== false && lt.canBeRequested !== false);
          offlineDataCache.set(CACHE_KEYS.LEAVE_TYPES, leaveTypesList);
          setLeaveTypes(leaveTypesList);
        }
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
        // If fetch fails and we don't have cached data, keep empty arrays
        // If we forced refresh and fetch failed, clear the state
        if (forceRefresh || !cachedStaff) {
          setStaff([]);
        }
        if (forceRefresh || !cachedLeaveTypes) {
          setLeaveTypes([]);
        }
      }
    } else {
      // Offline and no cache - show empty arrays
      if (!cachedStaff) {
        console.warn('[LeaveRequestForm] Offline and no cached staff available');
        setStaff([]);
      }
      if (!cachedLeaveTypes) {
        console.warn('[LeaveRequestForm] Offline and no cached leave types available');
        setLeaveTypes([]);
      }
    }
  };

  const handleCreateLeaveRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    const isOnline = navigator.onLine;
    const token = localStorage.getItem('token');
    
    if (!token) {
      setMessage(t('offline.noAuthToken') || 'No authentication token found');
      setMessageType('error');
      setIsLoading(false);
      return;
    }

    // Calculate total units from daily hours
    const selectedLeaveType = leaveTypes.find(lt => lt._id === selectedLeaveTypeId);
    const totalHours = calculateTotalHours();
    const numberOfUnits = selectedLeaveType?.typeOfUnits === 'days' 
      ? totalHours / 8 // Convert hours to days (8 hours = 1 day)
      : totalHours; // Keep as hours

    if (numberOfUnits <= 0) {
      setMessage('Please enter hours for at least one working day');
      setMessageType('error');
      setIsLoading(false);
      return;
    }

    const leaveRequestData = {
      staffId: selectedStaffId,
      leaveTypeId: selectedLeaveTypeId,
      startDate,
      endDate,
      numberOfUnits,
      typeOfUnits: selectedLeaveType?.typeOfUnits || 'days',
      description
    };

    try {
      // Use offlineSync to handle online/offline submission
      const result = await offlineSync.saveRequest(
        `${apiUrl}/api/leave-requests`,
        'POST',
        leaveRequestData,
        token
      );

      console.log('[LeaveRequestForm] Create result:', result);
      console.log('[LeaveRequestForm] Online status:', isOnline, 'offlineSync status:', offlineSync.getStatus());
      if (result.success) {
        console.log('[LeaveRequestForm] Leave request created successfully');
        if (isOnline) {
          setMessage(t('staff.leaveRequestCreated'));
          setMessageType('success');
          console.log('[LeaveRequestForm] Calling onSuccess callback');
          setTimeout(() => {
            if (onSuccess) {
              console.log('[LeaveRequestForm] Executing onSuccess callback');
              onSuccess();
            }
            console.log('[LeaveRequestForm] Closing form');
            onClose();
          }, 1500);
        } else {
          setMessage(t('offline.savedOffline') || 'Leave request saved offline. It will sync when connection is restored.');
          setMessageType('success');
          setTimeout(() => {
            if (onSuccess) {
              console.log('[LeaveRequestForm] Executing onSuccess callback (offline)');
              onSuccess();
            }
            console.log('[LeaveRequestForm] Closing form (offline)');
            onClose();
          }, 2000);
        }
      } else {
        console.error('[LeaveRequestForm] Create failed:', result.error);
        setMessage(result.error || t('staff.leaveRequestFailed'));
        setMessageType('error');
      }
    } catch (error) {
      console.error('Create leave request error:', error);
      setMessage(t('staff.leaveRequestFailed'));
      setMessageType('error');
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {t('staff.requestLeave') || 'Request Leave'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Message */}
          {message && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${messageType === 'success' ? 'bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 text-success-800 dark:text-success-400' : 'bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 text-error-800 dark:text-error-400'}`}>
              {message}
            </div>
          )}
          
          <form onSubmit={handleCreateLeaveRequest} className="space-y-4">
            {/* Staff Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('staff.title')} <span className="text-error-500">*</span>
              </label>
              <select
                value={selectedStaffId}
                onChange={(e) => setSelectedStaffId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select staff member</option>
                {staff.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.firstName} {s.lastName}
                  </option>
                ))}
              </select>
            </div>

            {/* Leave Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('staff.leaveType')} <span className="text-error-500">*</span>
              </label>
              <select
                value={selectedLeaveTypeId}
                onChange={(e) => setSelectedLeaveTypeId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">{t('staff.selectLeaveType')}</option>
                {leaveTypes.map((lt) => (
                  <option key={lt._id} value={lt._id}>
                    {lt.name} ({lt.isPaid ? 'Paid' : 'Unpaid'})
                  </option>
                ))}
              </select>
              
              {/* Leave Balance Display */}
              {selectedStaffId && selectedLeaveTypeId && (
                <div className="mt-2 p-2 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border border-accent-200 dark:border-accent-800 rounded-lg">
                  {(() => {
                    console.log('[LeaveRequestForm] Balance lookup:', {
                      selectedLeaveTypeId,
                      leaveBalancesCount: leaveBalances.length,
                      leaveBalances: leaveBalances.map((lb: any) => ({
                        leaveTypeIdObj: lb.leaveTypeId,
                        leaveTypeId: typeof lb.leaveTypeId === 'object' && lb.leaveTypeId !== null
                          ? lb.leaveTypeId._id || lb.leaveTypeId
                          : lb.leaveTypeId,
                        leaveTypeName: typeof lb.leaveTypeId === 'object' && lb.leaveTypeId !== null
                          ? lb.leaveTypeId.name
                          : 'Unknown',
                        balance: lb.balance,
                        typeOfUnits: lb.typeOfUnits
                      }))
                    });
                    
                    const balance = leaveBalances.find((lb: any) => {
                      const lbLeaveTypeId = typeof lb.leaveTypeId === 'object' && lb.leaveTypeId !== null
                        ? (lb.leaveTypeId._id || lb.leaveTypeId)
                        : lb.leaveTypeId;
                      const match = String(lbLeaveTypeId) === String(selectedLeaveTypeId);
                      if (match) {
                        console.log('[LeaveRequestForm] Found matching balance:', lb);
                      }
                      return match;
                    });
                    
                    console.log('[LeaveRequestForm] Final balance result:', balance);
                    
                    if (balance) {
                      return (
                        <p className="text-sm text-accent-800 dark:text-accent-200">
                          <span className="font-medium">Available Balance:</span> {balance.balance} {balance.typeOfUnits}
                        </p>
                      );
                    } else {
                      console.log('[LeaveRequestForm] No balance found for leaveTypeId:', selectedLeaveTypeId);
                      return (
                        <p className="text-sm text-accent-800 dark:text-accent-200">
                          <span className="font-medium">Available Balance:</span> Not found
                          {payrollActive && isFetchingPayrollBalance && (
                            <span className="text-xs opacity-75 ml-2">(fetching from payroll...)</span>
                          )}
                        </p>
                      );
                    }
                  })()}
                </div>
              )}
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('staff.startDate')} <span className="text-error-500">*</span>
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('staff.endDate')} <span className="text-error-500">*</span>
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  min={startDate}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Working Days Calendar */}
            {startDate && endDate && Object.keys(dailyHours).length > 0 && (() => {
              const periods = groupDaysByPayPeriod();
              return (
                <div>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {periods.map((period, periodIdx) => (
                      <div key={periodIdx} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        {/* Period Header */}
                        <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {period.periodLabel}
                          </h3>
                          <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                            {period.total.toFixed(1)} hrs
                          </span>
                        </div>
                        
                        {/* Calendar Grid */}
                        <div className="grid grid-cols-7 gap-2">
                          {/* Day Headers */}
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                            <div key={day} className="text-xs font-medium text-gray-500 dark:text-gray-400 text-center py-1">
                              {day}
                            </div>
                          ))}
                          
                          {/* Calendar Days */}
                          {(() => {
                            // Find first day of week for the period
                            const firstDay = new Date(period.days[0].date);
                            const firstDayOfWeek = firstDay.getDay();
                            const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Monday = 0
                            
                            // Create array for all days in the period range
                            const periodStart = new Date(period.days[0].date);
                            periodStart.setDate(periodStart.getDate() - startOffset);
                            const periodEnd = new Date(period.days[period.days.length - 1].date);
                            
                            const days: Array<{ date: string; hours: number | null; isWorkingDay: boolean }> = [];
                            const current = new Date(periodStart);
                            
                            while (current <= periodEnd) {
                              const dateStr = current.toISOString().split('T')[0];
                              const dayOfWeek = current.getDay();
                              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                              const workingDay = period.days.find(d => d.date === dateStr);
                              
                              days.push({
                                date: dateStr,
                                hours: workingDay ? workingDay.hours : null,
                                isWorkingDay: !isWeekend && !!workingDay
                              });
                              
                              current.setDate(current.getDate() + 1);
                            }
                            
                            // Pad to complete weeks
                            while (days.length % 7 !== 0) {
                              const lastDate = new Date(days[days.length - 1].date);
                              lastDate.setDate(lastDate.getDate() + 1);
                              days.push({
                                date: lastDate.toISOString().split('T')[0],
                                hours: null,
                                isWorkingDay: false
                              });
                            }
                            
                            return days.map((day, idx) => {
                              const isWeekend = new Date(day.date).getDay() === 0 || new Date(day.date).getDay() === 6;
                              const isOutsideRange = day.date < startDate || day.date > endDate;
                              
                              return (
                                <div
                                  key={idx}
                                  className={`relative aspect-square border rounded-lg p-1 ${
                                    day.isWorkingDay
                                      ? 'border-primary-300 dark:border-primary-600 bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50'
                                      : isWeekend || isOutsideRange
                                      ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-50'
                                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                                  }`}
                                >
                                  {day.isWorkingDay ? (
                                    <>
                                      <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                        {formatDayNumber(day.date)}
                                      </div>
                                      <input
                                        type="number"
                                        value={day.hours || ''}
                                        onChange={(e) => {
                                          const newHours = parseFloat(e.target.value) || 0;
                                          setDailyHours(prev => ({
                                            ...prev,
                                            [day.date]: newHours
                                          }));
                                        }}
                                        min="0"
                                        max="24"
                                        step="0.5"
                                        className="w-full px-1 py-0.5 text-xs text-center border border-primary-300 dark:border-primary-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                        placeholder="8"
                                      />
                                    </>
                                  ) : (
                                    <div className="text-xs text-gray-400 dark:text-gray-500">
                                      {formatDayNumber(day.date)}
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('staff.reason')} <span className="text-gray-400 text-xs">({t('common.optional')})</span>
              </label>
              <div className="relative">
                <textarea
                  value={showAIDescriptionPreview ? aiFormattedDescription : description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('staff.optionalReason')}
                  rows={3}
                  disabled={showAIDescriptionPreview}
                  className={`w-full px-3 py-2 pr-36 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-y min-h-[100px] ${
                    showAIDescriptionPreview ? 'bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50' : ''
                  }`}
                />
                <div className="absolute bottom-3 right-3 flex gap-2">
                  {description.trim() && !showAIDescriptionPreview && (
                    <button
                      type="button"
                      onClick={() => {
                        setDescription('');
                        setOriginalDescription('');
                        setAiFormattedDescription('');
                      }}
                      className="p-2 rounded-full bg-gray-400 hover:bg-gray-500 text-white transition-all"
                      title={t('timesheet.startAgain')}
                    >
                      <RotateCcw className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleStartRecordingDescription}
                    disabled={showAIDescriptionPreview || !isOnline}
                    className={`p-2 rounded-full transition-all relative ${
                      isRecordingDescription 
                        ? 'bg-error-500 text-white animate-pulse' 
                        : !isOnline
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-accent-500 hover:bg-accent-600 text-white disabled:opacity-50'
                    }`}
                    title={!isOnline 
                      ? t('timesheet.voiceTranscriptionOffline') || 'Voice transcription unavailable offline'
                      : t('timesheet.voiceTranscription')
                    }
                  >
                    <Mic className={`w-5 h-5 ${!isOnline ? 'line-through' : ''}`} />
                  </button>
                  <button
                    type="button"
                    onClick={handleFormatDescriptionWithAI}
                    disabled={showAIDescriptionPreview || isFormattingDescriptionAI || !isOnline}
                    className={`p-2 rounded-full transition-all ${
                      !isOnline
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                        : 'bg-purple-500 hover:bg-purple-600 text-white disabled:opacity-50'
                    }`}
                    title={!isOnline
                      ? t('timesheet.aiFormattingOffline') || 'AI formatting unavailable offline'
                      : t('timesheet.generateNotesWithAI')
                    }
                  >
                    {isFormattingDescriptionAI ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Sparkles className={`w-5 h-5 ${!isOnline ? 'line-through' : ''}`} />
                    )}
                  </button>
                </div>
              </div>

              {/* AI Preview Actions */}
              {showAIDescriptionPreview && (
                <>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={handleAcceptAIDescription}
                      className="flex-1 px-3 py-2 bg-success-500 hover:bg-success-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                    >
                      <Check className="w-4 h-4" />
                      {t('timesheet.accept')}
                    </button>
                    <button
                      type="button"
                      onClick={handleRevertAIDescription}
                      className="flex-1 px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                    >
                      <RotateCcw className="w-4 h-4" />
                      {t('timesheet.revert')}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('timesheet.aiFormattedVersion')}
                  </p>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50"
              >
                <Calendar className="w-4 h-4 mr-2" />
                {isLoading ? t('common.saving') : t('staff.requestLeave')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

